(function () {
  'use strict';

  var instances = new WeakMap();

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function PublidepGallery(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-tour-viewport]');
    this.progress = root.querySelector('[data-tour-progress]');
    this.thumb = root.querySelector('[data-tour-progress-thumb]');
    this.isMobile = root.classList.contains('pts-publidep-tour__gallery--mobile');
    this.step = this.isMobile ? 226.4375 : 325;
    this.thumbWidth = this.isMobile ? 40 : 116;
    this.scrollRaf = 0;
    this.dragState = null;
    this.thumbDragState = null;
    this.boundHandlers = {};
    this.canScroll = false;
  }

  PublidepGallery.prototype.getMaxScroll = function () {
    if (!this.viewport) return 0;
    return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
  };

  PublidepGallery.prototype.getMaxThumbLeft = function () {
    if (!this.progress) return 0;
    return Math.max(0, this.progress.clientWidth - this.thumbWidth);
  };

  PublidepGallery.prototype.updateProgressVisibility = function () {
    if (!this.progress) return;
    this.canScroll = this.getMaxScroll() > 1;
    this.progress.classList.toggle('is-hidden', !this.canScroll);
    if (!this.canScroll && this.thumb) {
      this.thumb.style.transform = 'translate3d(0px, 0, 0)';
      this.thumb.setAttribute('aria-valuenow', '0');
    }
  };

  PublidepGallery.prototype.syncThumb = function () {
    if (!this.viewport || !this.thumb || !this.progress || !this.canScroll) return;

    var maxScroll = this.getMaxScroll();
    var maxThumbLeft = this.getMaxThumbLeft();
    var scrollProgress = maxScroll > 0 ? this.viewport.scrollLeft / maxScroll : 0;
    var thumbLeft = scrollProgress * maxThumbLeft;

    this.thumb.style.transform = 'translate3d(' + thumbLeft + 'px, 0, 0)';
    this.thumb.setAttribute('aria-valuenow', String(Math.round(scrollProgress * 100)));
  };

  PublidepGallery.prototype.scheduleSyncThumb = function () {
    var self = this;
    if (self.scrollRaf) return;
    self.scrollRaf = window.requestAnimationFrame(function () {
      self.scrollRaf = 0;
      self.syncThumb();
    });
  };

  PublidepGallery.prototype.snapToNearest = function () {
    if (!this.viewport || !this.canScroll) return;

    var maxScroll = this.getMaxScroll();
    var index = Math.round(this.viewport.scrollLeft / this.step);
    var target = clamp(index * this.step, 0, maxScroll);

    this.viewport.scrollTo({
      left: target,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth'
    });
  };

  PublidepGallery.prototype.scrollToProgress = function (progress) {
    if (!this.viewport) return;

    var maxScroll = this.getMaxScroll();
    this.viewport.scrollLeft = clamp(progress, 0, 1) * maxScroll;
    this.scheduleSyncThumb();
  };

  PublidepGallery.prototype.scrollToIndex = function (index) {
    if (!this.viewport) return;

    var maxScroll = this.getMaxScroll();
    var target = clamp(index * this.step, 0, maxScroll);

    this.viewport.scrollTo({
      left: target,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth'
    });
  };

  PublidepGallery.prototype.getSlideCount = function () {
    if (!this.viewport) return 0;
    return this.viewport.querySelectorAll('.pts-publidep-tour__slide').length;
  };

  PublidepGallery.prototype.getCurrentIndex = function () {
    if (!this.viewport) return 0;
    return Math.round(this.viewport.scrollLeft / this.step);
  };

  PublidepGallery.prototype.onViewportScroll = function () {
    this.scheduleSyncThumb();
  };

  PublidepGallery.prototype.onViewportPointerDown = function (event) {
    if (!this.canScroll || event.button !== 0) return;

    this.dragState = {
      startX: event.clientX,
      startScrollLeft: this.viewport.scrollLeft,
      dragging: false,
      pointerId: event.pointerId
    };

    this.viewport.setPointerCapture(event.pointerId);
  };

  PublidepGallery.prototype.onViewportPointerMove = function (event) {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

    var deltaX = event.clientX - this.dragState.startX;

    if (!this.dragState.dragging && Math.abs(deltaX) >= 4) {
      this.dragState.dragging = true;
      this.viewport.classList.add('is-dragging');
    }

    if (this.dragState.dragging) {
      event.preventDefault();
      this.viewport.scrollLeft = this.dragState.startScrollLeft - deltaX;
    }
  };

  PublidepGallery.prototype.onViewportPointerUp = function (event) {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

    var wasDragging = this.dragState.dragging;

    if (this.viewport.hasPointerCapture(event.pointerId)) {
      this.viewport.releasePointerCapture(event.pointerId);
    }

    this.viewport.classList.remove('is-dragging');
    this.dragState = null;

    if (wasDragging) {
      this.viewport.dataset.suppressClick = 'true';
      window.setTimeout(function () {
        delete this.viewport.dataset.suppressClick;
      }.bind(this), 0);
      this.snapToNearest();
    }
  };

  PublidepGallery.prototype.onViewportClick = function (event) {
    if (this.viewport.dataset.suppressClick === 'true') {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  PublidepGallery.prototype.onProgressPointerDown = function (event) {
    if (!this.canScroll) return;

    if (event.target === this.thumb) return;

    var rect = this.progress.getBoundingClientRect();
    var clickX = event.clientX - rect.left;
    var maxThumbLeft = this.getMaxThumbLeft();
    var nextThumbLeft = clamp(clickX - this.thumbWidth / 2, 0, maxThumbLeft);
    var progress = maxThumbLeft > 0 ? nextThumbLeft / maxThumbLeft : 0;

    this.scrollToProgress(progress);
    this.snapToNearest();
  };

  PublidepGallery.prototype.onThumbPointerDown = function (event) {
    if (!this.canScroll || event.button !== 0) return;

    event.stopPropagation();

    this.thumbDragState = {
      startX: event.clientX,
      startThumbLeft: this.getCurrentThumbLeft(),
      pointerId: event.pointerId
    };

    this.viewport.classList.add('is-thumb-dragging');
    this.thumb.classList.add('is-dragging');
    this.thumb.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  PublidepGallery.prototype.getCurrentThumbLeft = function () {
    if (!this.thumb) return 0;
    var transform = window.getComputedStyle(this.thumb).transform;
    if (!transform || transform === 'none') return 0;
    var matrix = transform.match(/matrix\(([^)]+)\)/);
    if (matrix) {
      var values = matrix[1].split(',');
      return parseFloat(values[4]) || 0;
    }
    var matrix3d = transform.match(/matrix3d\(([^)]+)\)/);
    if (matrix3d) {
      var values3d = matrix3d[1].split(',');
      return parseFloat(values3d[12]) || 0;
    }
    return 0;
  };

  PublidepGallery.prototype.onThumbPointerMove = function (event) {
    if (!this.thumbDragState || event.pointerId !== this.thumbDragState.pointerId) return;

    var deltaX = event.clientX - this.thumbDragState.startX;
    var maxThumbLeft = this.getMaxThumbLeft();
    var nextThumbLeft = clamp(this.thumbDragState.startThumbLeft + deltaX, 0, maxThumbLeft);
    var progress = maxThumbLeft > 0 ? nextThumbLeft / maxThumbLeft : 0;

    this.thumb.style.transform = 'translate3d(' + nextThumbLeft + 'px, 0, 0)';
    this.scrollToProgress(progress);
    event.preventDefault();
  };

  PublidepGallery.prototype.onThumbPointerUp = function (event) {
    if (!this.thumbDragState || event.pointerId !== this.thumbDragState.pointerId) return;

    if (this.thumb.hasPointerCapture(event.pointerId)) {
      this.thumb.releasePointerCapture(event.pointerId);
    }

    this.viewport.classList.remove('is-thumb-dragging');
    this.thumb.classList.remove('is-dragging');
    this.thumbDragState = null;
    this.snapToNearest();
  };

  PublidepGallery.prototype.onThumbKeyDown = function (event) {
    if (!this.canScroll) return;

    var slideCount = this.getSlideCount();
    var currentIndex = this.getCurrentIndex();
    var handled = true;

    switch (event.key) {
      case 'ArrowRight':
        this.scrollToIndex(currentIndex + 1);
        break;
      case 'ArrowLeft':
        this.scrollToIndex(currentIndex - 1);
        break;
      case 'PageDown':
        this.viewport.scrollTo({
          left: clamp(this.viewport.scrollLeft + this.viewport.clientWidth, 0, this.getMaxScroll()),
          behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        });
        break;
      case 'PageUp':
        this.viewport.scrollTo({
          left: clamp(this.viewport.scrollLeft - this.viewport.clientWidth, 0, this.getMaxScroll()),
          behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        });
        break;
      case 'Home':
        this.scrollToIndex(0);
        break;
      case 'End':
        this.scrollToIndex(slideCount - 1);
        break;
      default:
        handled = false;
    }

    if (handled) {
      event.preventDefault();
      window.setTimeout(this.syncThumb.bind(this), prefersReducedMotion() ? 0 : 300);
    }
  };

  PublidepGallery.prototype.onResize = function () {
    this.updateProgressVisibility();
    this.syncThumb();
  };

  PublidepGallery.prototype.bind = function () {
    var self = this;

    if (!this.viewport || !this.progress || !this.thumb) return;

    this.boundHandlers.viewportScroll = function () {
      self.onViewportScroll();
    };
    this.boundHandlers.viewportPointerDown = function (event) {
      self.onViewportPointerDown(event);
    };
    this.boundHandlers.viewportPointerMove = function (event) {
      self.onViewportPointerMove(event);
    };
    this.boundHandlers.viewportPointerUp = function (event) {
      self.onViewportPointerUp(event);
    };
    this.boundHandlers.viewportPointerCancel = this.boundHandlers.viewportPointerUp;
    this.boundHandlers.viewportClick = function (event) {
      self.onViewportClick(event);
    };
    this.boundHandlers.progressPointerDown = function (event) {
      self.onProgressPointerDown(event);
    };
    this.boundHandlers.thumbPointerDown = function (event) {
      self.onThumbPointerDown(event);
    };
    this.boundHandlers.thumbPointerMove = function (event) {
      self.onThumbPointerMove(event);
    };
    this.boundHandlers.thumbPointerUp = function (event) {
      self.onThumbPointerUp(event);
    };
    this.boundHandlers.thumbPointerCancel = this.boundHandlers.thumbPointerUp;
    this.boundHandlers.thumbKeyDown = function (event) {
      self.onThumbKeyDown(event);
    };
    this.boundHandlers.resize = function () {
      self.onResize();
    };

    this.viewport.addEventListener('scroll', this.boundHandlers.viewportScroll, { passive: true });
    this.viewport.addEventListener('pointerdown', this.boundHandlers.viewportPointerDown);
    this.viewport.addEventListener('pointermove', this.boundHandlers.viewportPointerMove);
    this.viewport.addEventListener('pointerup', this.boundHandlers.viewportPointerUp);
    this.viewport.addEventListener('pointercancel', this.boundHandlers.viewportPointerCancel);
    this.viewport.addEventListener('click', this.boundHandlers.viewportClick, true);

    this.progress.addEventListener('pointerdown', this.boundHandlers.progressPointerDown);
    this.thumb.addEventListener('pointerdown', this.boundHandlers.thumbPointerDown);
    this.thumb.addEventListener('pointermove', this.boundHandlers.thumbPointerMove);
    this.thumb.addEventListener('pointerup', this.boundHandlers.thumbPointerUp);
    this.thumb.addEventListener('pointercancel', this.boundHandlers.thumbPointerCancel);
    this.thumb.addEventListener('keydown', this.boundHandlers.thumbKeyDown);

    this.resizeObserver = new ResizeObserver(this.boundHandlers.resize);
    this.resizeObserver.observe(this.viewport);
    this.resizeObserver.observe(this.progress);
  };

  PublidepGallery.prototype.unbind = function () {
    if (!this.viewport) return;

    this.viewport.removeEventListener('scroll', this.boundHandlers.viewportScroll);
    this.viewport.removeEventListener('pointerdown', this.boundHandlers.viewportPointerDown);
    this.viewport.removeEventListener('pointermove', this.boundHandlers.viewportPointerMove);
    this.viewport.removeEventListener('pointerup', this.boundHandlers.viewportPointerUp);
    this.viewport.removeEventListener('pointercancel', this.boundHandlers.viewportPointerCancel);
    this.viewport.removeEventListener('click', this.boundHandlers.viewportClick, true);

    if (this.progress) {
      this.progress.removeEventListener('pointerdown', this.boundHandlers.progressPointerDown);
    }

    if (this.thumb) {
      this.thumb.removeEventListener('pointerdown', this.boundHandlers.thumbPointerDown);
      this.thumb.removeEventListener('pointermove', this.boundHandlers.thumbPointerMove);
      this.thumb.removeEventListener('pointerup', this.boundHandlers.thumbPointerUp);
      this.thumb.removeEventListener('pointercancel', this.boundHandlers.thumbPointerCancel);
      this.thumb.removeEventListener('keydown', this.boundHandlers.thumbKeyDown);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.scrollRaf) {
      window.cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = 0;
    }
  };

  PublidepGallery.prototype.init = function () {
    this.bind();
    this.updateProgressVisibility();
    this.syncThumb();
  };

  PublidepGallery.prototype.destroy = function () {
    this.unbind();
    this.dragState = null;
    this.thumbDragState = null;
  };

  function initGallery(root) {
    if (!root || instances.has(root)) return;

    var instance = new PublidepGallery(root);
    instance.init();
    instances.set(root, instance);
  }

  function destroyGallery(root) {
    var instance = instances.get(root);
    if (!instance) return;

    instance.destroy();
    instances.delete(root);
  }

  function boot(container) {
    var scope = container || document;
    scope.querySelectorAll('[data-pts-publidep-gallery]').forEach(initGallery);
  }

  function handleSectionLoad(event) {
    boot(event.target);
  }

  function handleSectionUnload(event) {
    event.target.querySelectorAll('[data-pts-publidep-gallery]').forEach(destroyGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
})();
